import "whatwg-fetch";
import history from "./history";

export interface RequestOptions extends RequestInit {
  // additional helpers used in makeReqOptions / request()
  contentType?: string;
  apiKey?: string;
  queryParams?: Record<string, string | number>;
  payload?: Record<string, any>;
  data?: any;
}

/**
 * Parse a JSON response. 204/205 codes return `null`.
 */
function parseJSON(response: Response): any | null {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.json();
}

/**
 * Basic status checker.  Redirects on certain codes, throws on
 * anything >=500.
 */
function checkStatus(response: Response): Response {
  // if (401 === response.status) {
  //   history.push("/signin");
  //   return response;
  // }

  if (response.status >= 200 && response.status < 500) {
    return response;
  }

  if ([502, 503, 403, 404].includes(response.status)) {
    history.push("/error_page");
  }

  const error: any = new Error(response.statusText);
  error.response = response;
  throw error;
}

function checkResponseContentType(response: Response): Promise<any> | Response {
  const ct = response.headers.get("content-type");
  if (ct && ct.includes("application/json")) {
    return parseJSON(response);
  }
  return response;
}

/**
 * Build a RequestOptions object from a loose params bag.
 */
export function makeReqOptions(params: Partial<RequestOptions> = {}): RequestOptions {
  const options: RequestOptions = {
    method: "GET",
    headers: {
      Accept: "*/*",
    },
  };

  if (params.contentType && params.contentType !== "multipart/form-data") {
    (options.headers as Record<string, string>)["Content-Type"] = params.contentType;
  } else if (!params.contentType) {
    (options.headers as Record<string, string>)["Content-Type"] = "application/json";
  }

  if (params.apiKey) {
    (options.headers as Record<string, string>)["X-Api-Key"] = params.apiKey;
  }

  if (params.method) {
    options.method = params.method;
  }

  if (params.credentials) {
    options.credentials = params.credentials;
  }

  options.mode = "cors";

  if (
    (params.method === "POST" ||
      params.method === "PUT" ||
      params.method === "PATCH") &&
    params.data
  ) {
    const contentType = (options.headers as Record<string, string>)["Content-Type"];
    if (contentType === "application/x-www-form-urlencoded") {
      const searchParams = Object.keys(params.data)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(params.data[key])}`
        )
        .join("&");
      options.body = searchParams;
    } else if (contentType === "application/json") {
      options.body = JSON.stringify(params.data);
    } else {
      // multipart, blobs, etc.
      options.body = params.data;
      // options.mode = 'no-cors';
    }
  } else if (params.data) {
    options.queryParams = params.data;
  }
  if (params.payload) {
    options.queryParams = params.payload;
  }

  return options;
}

/**
 * Perform the request, appending query parameters if provided.
 */
export default function request(url: string, options: RequestOptions): Promise<any> {
  const requestURL = new URL(url);
  if (options.queryParams) {
    Object.keys(options.queryParams).forEach((key) => {
      requestURL.searchParams.append(
        key,
        String(options.queryParams![key])
      );
    });
  }
  return fetch(requestURL.toString(), options)
    .then(checkStatus)
    .then(checkResponseContentType);
}